import { ExportProgressReporter } from "../components/CustomButton";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { toast } from "react-toastify";
import CustomLabel from "../components/CustomLabel";
import PageHeader from "../components/PageHeader";
import {
  ADMIN_DATA_SUBTITLES,
  adminNavItemSx,
  adminNavPanelSx,
  adminPageSx,
  adminNavTitleSx,
} from "../components/adminData/adminNavStyles";
import CustomSelect from "../components/CustomSelect";
import CustomTable from "../components/table/CustomTable";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import Actions from "../components/Actions";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import InvoiceItemsEditor from "../components/invoices/InvoiceItemsEditor";
import { COLORS } from "../assets/theme";
import { IPageable, Params } from "../types/table/interfaces.type";
import { getAllClients, getClientById } from "../services/ClientsService";
import { getUserId } from "../services/authService";
import {
  getAllInvoices,
  getInvoiceById,
} from "../services/newInvoicesService";
import { getAllBusinesses } from "../services/VenueService";
import PinAccessDialog from "../components/PinAccessDialog";
import {
  getCachedPinAccess,
  subscribeToPinAccess,
} from "../utils/pinAccessCache";
import { formatTableDate, formatTableDateTime } from "../utils/tableDateFormat";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

type AdminTab = "clients" | "finance" | "expenses" | "reports" | "notifications";

interface AdminFinanceRow {
  id: string;
  Invoice_number?: string;
  client_name?: string;
  mobile?: string;
  invoice_date?: string;
  created_at?: string;
  paid_at?: string;
  venue_name?: string;
  venue_id?: string | number;
  branch?: string;
  status?: string;
  method?: string;
  services?: string;
  total_amount?: string | number;
  net_amount?: string | number;
  notes?: string;
  business_id?: string | number;
  invoice?: any;
  [key: string]: any;
}

type InvoiceClientOption = {
  id: string;
  name: string;
  email?: string;
  mobile?: string;
};

const pickNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return undefined;
};

const toTitleCase = (value: string): string =>
  value
    .toLowerCase()
    .split(/\s+/)
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : ""))
    .join(" ");

const STATUS_FORM_VALUE_MAP: Record<
  1 | 2 | 3,
  "paid" | "unpaid" | "cancelled"
> = {
  1: "paid",
  2: "unpaid",
  3: "cancelled",
};

const STATUS_LABEL_MAP: Record<1 | 2 | 3, string> = {
  1: "Paid",
  2: "Unpaid",
  3: "Cancelled",
};

const PAYMENT_METHOD_LABEL_MAP: Record<0 | 1 | 2 | 3 | 4, string> = {
  0: "Cash",
  1: "Card",
  2: "Cash",
  3: "Bank Transfer",
  4: "Online Payment",
};

const resolveStatusLabel = (entry: any): string => {
    const rawStatus = entry?.status || entry?.invoice?.status || "PENDING";
    const statusString = String(rawStatus).toUpperCase();
  
  // Map backend status strings to display labels
  const statusLabelMap: Record<string, string> = {
    "PAID": "Paid",
    "PENDING": "Unpaid",
    "DRAFT": "Unpaid",
    "APPROVED": "Paid",
      "PARTIALLY_PAID": "Partially Paid",
      "PARTIALLY_REFUNDED": "Partially Refunded",
      "REFUNDED": "Refunded",
      "VOID": "Cancelled",
      "VOIDED": "Cancelled",
      "REJECTED": "Cancelled",
      "CANCELLED": "Cancelled",
    };

    return statusLabelMap[statusString] || toTitleCase(statusString);
  };

const resolveStatusFormValue = (
  status: unknown,
  fallbackId?: unknown
): "paid" | "unpaid" | "cancelled" => {
  const numeric = Number(status ?? fallbackId);
  if (Number.isFinite(numeric)) {
    const formValue = STATUS_FORM_VALUE_MAP[numeric as 1 | 2 | 3];
    if (formValue) {
      return formValue;
    }
  }

  if (typeof status === "string" && status.trim()) {
    const normalized = status.trim().toLowerCase();
    if (
      normalized === "paid" ||
      normalized === "unpaid" ||
      normalized === "cancelled"
    ) {
      return normalized;
    }
  }

  if (typeof fallbackId === "string" && fallbackId.trim()) {
    const normalized = fallbackId.trim().toLowerCase();
    if (
      normalized === "paid" ||
      normalized === "unpaid" ||
      normalized === "cancelled"
    ) {
      return normalized as "paid" | "unpaid" | "cancelled";
    }
  }

  return "unpaid";
};
const resolvePaymentMethodLabel = (entry: any): string => {
  const invoice = entry?.invoice ?? {};
  const methodId = Number(
    entry?.payment_method_id ??
      invoice?.payment_method_id ??
      invoice?.payment_method?.id
  );
  if (Number.isFinite(methodId)) {
    const label = PAYMENT_METHOD_LABEL_MAP[methodId as 0 | 1 | 2 | 3 | 4];
    if (label) {
      return label;
    }
  }

  return "Cash";
};

const resolveInvoicePaidAt = (entry: any): string => {
  const invoice = entry?.invoice ?? {};
  const latestPayment = Array.isArray(entry?.payments)
    ? entry.payments[entry.payments.length - 1]
    : Array.isArray(invoice?.payments)
    ? invoice.payments[invoice.payments.length - 1]
    : null;

  return (
    entry?.paid_at ||
    entry?.paidAt ||
    entry?.payment_date ||
    entry?.paymentDate ||
    invoice?.paid_at ||
    invoice?.paidAt ||
    latestPayment?.paid_at ||
    latestPayment?.paidAt ||
    latestPayment?.created_at ||
    latestPayment?.createdAt ||
    ""
  );
};

const normalizeInvoiceRow = (item: any): AdminFinanceRow => {
  return {
    id: String(item.invoiceId),
    Invoice_number: item.invoiceId,
    client_name: item.client?.name || item.clientName || item.clientOrgUserId || "N/A",
    mobile: item.client?.phone || "N/A",

    invoice_date: item.createdAt
      ? formatTableDate(item.createdAt)
      : "N/A",
    created_at: item.createdAt,
    paid_at: resolveInvoicePaidAt(item),
    venue_name: item.business?.name || item.business?.id || "N/A",
    venue_id: item.business?.id || null,
    branch: item.unit?.name || item.unit?.id || "N/A",
    status: item.status,
    method: String(item.paymentMethod || item.method || item.payment_method || "cash"),
    services: (item.services || [])
      .map((s: any) => s.name || "Service")
      .join(", ") || "N/A",
    total_amount: item.amount,
    net_amount: item.netAmount ?? item.amount,
    notes: item.notes || "N/A",
    business_id: item.business?.id || null,
    raw: item, 
  };
};

const parseDateValue = (value?: string): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const match = value.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s*(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (!match) return null;
  const [, day, month, year, hour, minute, second] = match;
  const d = Number(day);
  const m = Number(month) - 1;
  const y = Number(year);
  const h = Number(hour || "0");
  const min = Number(minute || "0");
  const sec = Number(second || "0");
  const date = new Date(y, m, d, h, min, sec);
  return Number.isNaN(date.getTime()) ? null : date;
};
const ADMIN_INVOICES_PAGE_SIZE = 30;

const AdminInvoices: React.FC = () => {
  const navigate = useNavigate();
  const activeTab: AdminTab = "finance";
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [invoices, setInvoices] = useState<AdminFinanceRow[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [financeUnlocked, setFinanceUnlocked] = useState(() =>
    getCachedPinAccess()
  );
  const [pinDialogOpen, setPinDialogOpen] = useState(() =>
    !getCachedPinAccess()
  );
  const [invoiceEditOpen, setInvoiceEditOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<any | null>(null);
  const [invoiceConfirmOpen, setInvoiceConfirmOpen] = useState(false);
  const [invoiceToDeleteId, setInvoiceToDeleteId] = useState<
    string | number | null
  >(null);
  const [invoiceDateRange, setInvoiceDateRange] = useState<
    { start?: string; end?: string } | [string?, string?] | null
  >(() => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
    return { start: start.toISOString(), end: end.toISOString() };
  });
  const [invoiceStatusFilter, setInvoiceStatusFilter] =
    useState<string>("all");
  const [invoiceServices, setInvoiceServices] = useState<any[]>([]);
  const [invoiceClients, setInvoiceClients] = useState<InvoiceClientOption[]>(
    []
  );
  const buildDateBoundary = (raw: string | Date | null | undefined, endOfDay: boolean) => {
    if (!raw) return null;
    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split("-").map(Number);
      return new Date(Date.UTC(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0));
    }
    const parsed = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(
      Date.UTC(
        parsed.getUTCFullYear(),
        parsed.getUTCMonth(),
        parsed.getUTCDate(),
        endOfDay ? 23 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 999 : 0,
      ),
    );
  };
  const [venues, setVenues] = useState<{ id: string; name: string }[]>([]);
  const [venueFilter, setVenueFilter] = useState<string>("all");
  const navigateTo = useCallback(
    (path: string) => {
      if (!financeUnlocked) {
        setPinDialogOpen(true);
        return;
      }
      navigate(path);
    },
    [financeUnlocked, navigate]
  );

  useEffect(() => {
const fetchBusinesses = async () => {
  try {
    const response = await getAllBusinesses();

    const businesses =
        response?.data?.businesses ||
        response?.businesses ||
        (Array.isArray(response) ? response : []);

      const mapped = businesses
        .map((b: any) => {
          const id = b?.id || b?._id;
          if (!id) return null;

          const name =
            pickNonEmptyString(b?.name, b?.title) || `Business ${id}`;

          return { id: String(id), name: String(name) };
        })
        .filter(Boolean) as { id: string; name: string }[];

      setVenues(mapped);
    } catch (error) {
      console.error("Error fetching businesses:", error);
      setVenues([]);
    }
  };

  fetchBusinesses();
}, []);

useEffect(() => {
  const unsubscribe = subscribeToPinAccess((value) => {
    setFinanceUnlocked(value);
    if (!value) setPinDialogOpen(true);
  });
  return unsubscribe;
}, []);

useEffect(() => {
  if (financeUnlocked) setPinDialogOpen(false);
}, [financeUnlocked]);

const handlePinVerified = () => {
  setFinanceUnlocked(true);
  setPinDialogOpen(false);
};

const resolveBackendStatusFilter = (value: any): string | undefined => {
  const v = String(value || "").trim();
  if (!v || v === "all") return undefined;
  if (v === "Paid") return "PAID";
  if (v === "Unpaid") return "PENDING";
  if (v === "Cancelled") return "CANCELLED";
  return undefined;
};

// One page of invoices; venue, status, dates and search are filtered by the
// server, which also sums the amounts over every match.
const loadInvoicesPage = useCallback(
  async ({
    page,
    size,
    search: searchTerm,
    withSummary,
  }: {
    page: number;
    size: number;
    search?: string;
    withSummary?: boolean;
  }) => {
    const params: any = { page, size };

    if (venueFilter !== "all") {
      params.businessId = venueFilter;
    }

    const startDate = buildDateBoundary(
      Array.isArray(invoiceDateRange) ? invoiceDateRange[0] : (invoiceDateRange as any)?.start,
      false
    );
    const endDate = buildDateBoundary(
      Array.isArray(invoiceDateRange) ? invoiceDateRange[1] : (invoiceDateRange as any)?.end,
      true
    );

    if (startDate) params.from = startDate.toISOString().split("T")[0];
    if (endDate) params.to = endDate.toISOString().split("T")[0];
    // The date column shows when the invoice was created; keep only rows whose
    // shown date is in the range, as this screen always has.
    if (startDate || endDate) params.matchCreatedDate = "true";

    const backendStatus = resolveBackendStatusFilter(invoiceStatusFilter);
    if (backendStatus) params.status = backendStatus;
    const trimmedSearch = String(searchTerm || "").trim();
    if (trimmedSearch) params.search = trimmedSearch;
    if (withSummary) params.summary = "true";

    const response: any = await getAllInvoices(params);

    const api = response?.data || response;
    const invoicesData =
      api?.invoices ||
      api?.content ||
      api?.data?.invoices ||
      (Array.isArray(api) ? api : []);

    const rows: AdminFinanceRow[] = invoicesData.map((item: any, index: number) => {
      const row = normalizeInvoiceRow(item);
      if (!row.id || row.id === "undefined") {
        row.id = `invoice-owner-${page}-${index}`;
      }
      return row;
    });
    return {
      rows,
      totalElements: Number(api?.totalElements) || rows.length,
      totalPages: Math.max(1, Number(api?.totalPages) || 1),
      summary: api?.summary ?? null,
    };
  },
  [venueFilter, invoiceDateRange, invoiceStatusFilter]
);


  const [invoicesSummary, setInvoicesSummary] = useState<any | null>(null);
  const debouncedSearch = useDebouncedValue(search, 400);
  // Total of invoices already PAID or APPROVED, over every matching invoice.
  const paidNetTotal = Number(invoicesSummary?.paidInvoicesTotal) || 0;

  const fetchFinance = useCallback(
    async (params: Params<AdminFinanceRow>): Promise<IPageable<AdminFinanceRow>> => {
      const page = Number(params.page) || 0;
      const size = Number(params.size) || ADMIN_INVOICES_PAGE_SIZE;
      if (!financeUnlocked) {
        return { content: [], number: page, size, totalElements: 0, totalPages: 1 };
      }
      setLoadingInvoices(true);
      try {
        const result = await loadInvoicesPage({
          page,
          size,
          search: String(params.search || ""),
          withSummary: true,
        });
        setInvoices(result.rows);
        setInvoicesSummary(result.summary);
        setTotalElements(result.totalElements);
        return {
          content: result.rows,
          number: page,
          size,
          totalElements: result.totalElements,
          totalPages: result.totalPages,
        };
      } catch (error) {
        console.error("Error fetching invoices:", error);
        setInvoices([]);
        setInvoicesSummary(null);
        throw error;
      } finally {
        setLoadingInvoices(false);
      }
    },
    [financeUnlocked, loadInvoicesPage]
  );

  const exportFinanceCsv = useCallback(async (reportProgress?: ExportProgressReporter) => {
    // The table holds one page; the file gets every matching invoice.
    const filteredFinance: AdminFinanceRow[] = [];
    try {
      for (let page = 0; page < 1000; page += 1) {
        const result = await loadInvoicesPage({ page, size: 200, search: debouncedSearch });
        filteredFinance.push(...result.rows);
        reportProgress?.(page + 1, result.totalPages);
        if (page + 1 >= result.totalPages || result.rows.length === 0) break;
      }
    } catch (error) {
      console.error("Error exporting invoices:", error);
      return;
    }
    if (filteredFinance.length === 0) return;
    const keys = [
      "Invoice_number",
      "client_name",
      "mobile",
      "invoice_date",
      "created_at",
      "paid_at",
      "venue_name",
      "branch",
      "status",
      "method",
      "services",
      "net_amount",
      "notes",
    ];
    const header = keys.join(",");
    const lines = filteredFinance.map((r: any) => {
      const row: Record<string, any> = {
        Invoice_number: r.Invoice_number ?? "-",
        client_name: r.client_name ?? "-",
        mobile: r.mobile ?? "-",
        invoice_date: r.invoice_date ?? "-",
        created_at: r.created_at ?? "-",
        paid_at: r.paid_at ?? "-",
        venue_name: r.venue_name ?? "-",
        branch: r.branch ?? "-",
        status: r.status ?? "-",
        method: r.method ?? "-",
        services: r.services ?? "-",
        net_amount: r.net_amount ?? "-",
        notes: r.notes ?? "-",
      };
      return keys
        .map((k) => `"${String(row[k] ?? "-").replace(/"/g, '""')}"`)
        .join(",");
    });
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [debouncedSearch, loadInvoicesPage]);


 
  const handleInvoicePdf = useCallback(
    (id: string | number) => {
      const match = invoices.find((row) => String(row.id) === String(id));
      const branchId =
        match?.business_id ||
        match?.invoice?.business_id ||
        match?.businessId;
      if (!branchId) {
        toast.error("Missing venue id for invoice PDF");
        return;
      }
      navigate(`/dashboard/${branchId}/invoicepdf/${id}`);
    },
    [invoices, navigate]
  );

  const invoiceActions = Actions({
  
    onPdf: {
      handler: (id: number | string) => handleInvoicePdf(id),
    },
  });
  const invoiceFormFields: Field[] = [
    {
      name: "clientName",
      label: "Client Name",
      colSpan: 1,
      customRender: (value: string, onChange: (v: string) => void, helpers) => {
        const invoiceClientsFromRows = invoices
          .map((inv: any) =>
            inv.client_id && inv.client_name
              ? {
                  id: inv.client_id,
                  name:
                    inv.client_name ||
                    inv.client?.name ||
                    inv.client?.full_name ||
                    inv.client?.fullName,
                  email: inv.client?.email || inv.email,
                  mobile: inv.client?.mobile || inv.mobile,
                }
              : null
          )
          .filter(Boolean) as InvoiceClientOption[];

        const mergedClients = [
          ...(Array.isArray(invoiceClients) ? invoiceClients : []),
          ...invoiceClientsFromRows,
        ].reduce((acc, c: any) => {
          const clientId =
            c?.id ??
            c?.value ??
            c?.client_id ??
            c?._id ??
            c?.user_id ??
            c?.clientId;
          if (!clientId) return acc;
          const key = String(clientId);
          if (!acc.has(key)) {
            acc.set(key, {
              id: key,
              name:
                c?.name ||
                c?.label ||
                c?.client_name ||
                c?.fullName ||
                c?.full_name ||
                c?.email ||
                `Client ${key}`,
              email: c?.email || "",
              mobile:
                c?.mobile ||
                c?.phone ||
                c?.phoneNumber ||
                c?.mobileNumber ||
                "",
            });
          }
          return acc;
        }, new Map<string, InvoiceClientOption>());

        const clientList: InvoiceClientOption[] = Array.from(
          mergedClients.values()
        );

        const options = clientList.map((client: any) => ({
          label: client.name,
          value: client.id,
        }));
        return (
          <>
            <CustomLabel
              sx={{ p: 0.1 }}
              text={"Client Name"}
              color={COLORS.typographyColor}
              fontWeight={500}
              fontSize={14}
            />
            <CustomSelect
              label="Client Name"
              value={value || ""}
              options={options}
              onChange={(v: any) => {
                onChange(String(v || ""));
                if (v) {
                  const selected = clientList.find(
                    (c) => String(c.id) === String(v)
                  );
                  if (selected) {
                    helpers?.setFieldValue("email", selected.email || "");
                    helpers?.setFieldValue(
                      "phoneNumber",
                      selected.mobile || ""
                    );
                    return;
                  }
                  getClientById(String(v))
                    .then((client) => {
                      if (client && client.user) {
                        helpers?.setFieldValue("email", client.user.email || "");
                        helpers?.setFieldValue(
                          "phoneNumber",
                          client.user.mobile || ""
                        );
                      }
                    })
                    .catch(console.error);
                }
              }}
              fullWidth
              placeholder="Select..."
            />
          </>
        );
      },
    },
    { name: "email", label: "Email" },
    { name: "phoneNumber", label: "Phone Number" },
    { name: "invoice_date", label: "Date", type: "date" },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: ["paid", "cancelled", "unpaid"],
    },
    {
      name: "paymentMethod",
      label: "Payment method",
      type: "dropdown",
      options: ["cash"],
      disabled: true,
    },
    { name: "notes", label: "Notes", colSpan: 2 },
    {
      name: "items",
      label: "Items",
      colSpan: 2,
      customRender: (_value: any, onChange: (v: any) => void, helpers) => (
        <InvoiceItemsEditor
          value={_value}
          onChange={onChange}
          vatValue={helpers?.values?.vat || 0}
          onVatChange={(v) => helpers?.setFieldValue("vat", v)}
          discountValue={helpers?.values?.discount || 0}
          onDiscountChange={(v) => helpers?.setFieldValue("discount", v)}
          isEditMode={!!editingInvoice}
          servicesList={invoiceServices}
        />
      ),
    },
  ];

  const navItems: { key: AdminTab; label: string; icon: React.ReactNode }[] = [
    { key: "clients", label: "Clients", icon: <PeopleIcon /> },
    { key: "finance", label: "Invoices", icon: <AccountBalanceWalletOutlinedIcon /> },
    { key: "expenses", label: "Expenses", icon: <ReceiptLongOutlinedIcon /> },
    { key: "reports", label: "Reports", icon: <AssessmentOutlinedIcon /> },
    {
      key: "notifications",
      label: "Notification Devices",
      icon: <NotificationsActiveOutlinedIcon />,
    },
  ];
  const activeTabLabel =
    navItems.find((item) => item.key === activeTab)?.label || "Admin Data";
  return (
    <Box
      sx={adminPageSx}
    >
      <Box
        sx={adminNavPanelSx(sidebarCollapsed)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: sidebarCollapsed ? "center" : "space-between",
            px: sidebarCollapsed ? 0 : 0.5,
            mb: 1,
          }}
        >
          {!sidebarCollapsed && (
            <CustomLabel text="Admin Data" variant="h6" sx={adminNavTitleSx} />
          )}
          <IconButton
            size="small"
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            sx={{ color: COLORS.MainColor }}
          >
            {sidebarCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        <List disablePadding>
          {navItems.map((item) => {
            const selected = activeTab === item.key;
            return (
              <Tooltip
                key={item.key}
                title={sidebarCollapsed ? item.label : ""}
                placement="right"
              >
                <ListItemButton
                  selected={selected}
                  onClick={() => {
                    if (item.key === "clients") {
                      navigate("/all-data");
                    } else if (item.key === "expenses") {
                      navigateTo("/all-data/expenses");
                    } else if (item.key === "reports") {
                      navigateTo("/all-data/reports");
                    } else if (item.key === "notifications") {
                      navigate("/all-data/notification-devices");
                    }
                  }}
                  sx={adminNavItemSx(selected, sidebarCollapsed)}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: sidebarCollapsed ? "auto" : 32,
                      color: "inherit",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: "0.9rem" }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      <Box
        sx={{
          flex: 1,
          px: { xs: 1.5, sm: 2.5 },
          // Page is pulled up under the header (adminPageSx); give it back here.
          pt: { xs: 3, sm: 5 },
          pb: { xs: 2, sm: 2.5 },
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <PageHeader
          title={activeTabLabel}
          subtitle={ADMIN_DATA_SUBTITLES[activeTab]}
          icon={navItems.find((item) => item.key === activeTab)?.icon}
        />

        <Box
          sx={{
            backgroundColor: COLORS.WhiteBackground,
            borderRadius: 2,
            p: { xs: 1.5, sm: 2 },
            boxShadow: "0px 4px 12px rgba(0,0,0,0.04)",
          }}
        >
          <SearchHeaderContainer
            search={search}
            setSearch={setSearch}
            selectConfig={{
              options: [
                { label: "All", value: "all" },
                ...venues.map((venue) => ({
                  label: venue.name,
                  value: venue.id,
                })),
              ],
              value: venueFilter,
              onChange: (v: any) => {
                const next =
                  v === null || v === undefined || v === "" ? "all" : String(v);
                setVenueFilter(next);
              },
              placeholder: "Venue",
              label: "Venue",
              size: "small",
              searchable: false,
              clearable: true,
              sx: { width: 200 },
            }}
            dateRangeConfig={{
              value: invoiceDateRange ?? undefined,
              onChange: (v: any) => setInvoiceDateRange(v),
              fullWidth: false,
            }}
            selectConfig2={{
              options: [
                { label: "All", value: "all" },
                { label: "Paid", value: "Paid" },
                { label: "Unpaid", value: "Unpaid" },
                { label: "Cancelled", value: "Cancelled" },
              ],
              value: invoiceStatusFilter,
              onChange: (v: any) => setInvoiceStatusFilter(v),
              placeholder: "Status",
              label: "Status",
              size: "small",
              searchable: false,
              clearable: true,
              sx: { width: 180 },
            }}
            buttonConfig={[
              {
                label: "Export CSV",
                onClick: (reportProgress) => exportFinanceCsv(reportProgress),
                startIcon: <FileDownloadIcon />,
                variant: "contained",
                bgColor: COLORS.MainColor,
                textColor: "#fff",
                showExportProgress: true,
                disabled: !financeUnlocked,
                sx: {
                  width: { xs: "100%", sm: "auto" },
                  marginRight: { xs: 1.5, sm: 0 },
                },
              },
            ]}
          />

          <Box sx={{ mt: 2 }}>
            <CustomTable<AdminFinanceRow>
              filters={{
                page: 0,
                size: ADMIN_INVOICES_PAGE_SIZE,
                search: debouncedSearch,
                venueFilter,
                invoiceStatusFilter,
                dateRange: JSON.stringify(invoiceDateRange ?? null),
                financeUnlocked,
              }}
              fields={[
                { key: "Invoice_number", header: "Invoice #" },
                { key: "client_name", header: "Client Name" },
                { key: "mobile", header: "Phone Number" },
                { key: "invoice_date", header: "Appointment Date" },
                { key: "venue_name", header: "Venue" },
                { key: "branch", header: "Branch" },
            {
              key: "status",
              header: "Status",
              render: (row: any) => {
                  const raw = String(row.status || "").toUpperCase();
                  const isPending = raw === "PENDING";
                  const isPaid = raw === "PAID" || raw === "APPROVED";
                  const isCancelled = raw === "CANCELLED" || raw === "VOID" || raw === "VOIDED" || raw === "REJECTED";
                const bg = isPending
                  ? COLORS.MainColor
                  : isPaid
                  ? "#4caf50"
                  : isCancelled
                  ? COLORS.RED
                  : "#ff9800"; // orange for others
                const color = "#fff";
                  const displayStatus =
                    raw === "VOID" || raw === "VOIDED" ? "CANCELLED" : raw;
                  const label = displayStatus
                    ? displayStatus
                        .toLowerCase()
                        .split(/[_\s-]+/)
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                      .join(" ")
                  : "Status";
                return (
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 10px",
                      borderRadius: 12,
                      backgroundColor: bg,
                      color,
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      textTransform: "capitalize",
                    }}
                  >
                    {label}
                  </span>
                );
              },
            },
                {
                  key: "created_at",
                  header: "Created At",
                  render: (row: any) => {
                    const dateStr = row.created_at || row.createdAt;
                    if (!dateStr) return "-";
                    return formatTableDateTime(dateStr);
                  },
                },
                {
                  key: "paid_at",
                  header: "Paid At",
                  render: (row: any) => {
                    const dateStr = row.paid_at || row.paidAt;
                    if (!dateStr) return "-";
                    return formatTableDateTime(dateStr);
                  },
                },
                { key: "services", header: "Services" },
                { key: "net_amount", header: "Net Amount" },
                { key: "notes", header: "Notes" },
              ]}
              fetchFunc={fetchFinance}
              loading={loadingInvoices}
              actions={invoiceActions}
            />
          </Box>
          <Box
            sx={{
              backgroundColor: COLORS.WhiteBackground,
              borderRadius: "0 0 10px 10px",
              padding: "16px",
              marginTop: "-10px",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <CustomLabel
              text="Total Net Amount:"
              sx={{ color: COLORS.typographyColor }}
            />
            <CustomLabel
              text={new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(paidNetTotal)}
              fontWeight="bold"
              sx={{ color: COLORS.MainColor }}
            />
          </Box>
        </Box>
      </Box>
      <PinAccessDialog
        open={pinDialogOpen}
        onClose={() => setPinDialogOpen(false)}
        onVerified={handlePinVerified}
        title="Finance access"
        description="Enter the PIN to view admin invoices."
      />

    </Box>
  );
};

export default AdminInvoices;
